import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * ASYCO: timer del cierre diferido. El cierre inmediato en pointerleave hacía
   * desaparecer el mega-menú al cruzar el gap entre el trigger y el dropdown.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimer = null;

  /**
   * ASYCO: swap diferido entre ítems. Ir en diagonal del trigger al dropdown
   * cruza otros triggers ("OFERTAS", "Colchones"); el swap inmediato robaba el
   * menú a mitad de camino. Un cruce fugaz (<150ms) ya no cambia el menú.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #swapTimer = null;

  /** @type {HTMLElement | null} */
  #pendingItem = null;

  /**
   * ASYCO: dirección reciente del puntero (hover intent). Si el mouse viene
   * BAJANDO hacia el dropdown, el swap espera más; horizontal = swap ágil.
   * @type {{y: number} | null}
   */
  #lastPointer = null;

  /** @type {{y: number} | null} */
  #prevPointer = null;

  #trackPointer = (event) => {
    this.#prevPointer = this.#lastPointer;
    this.#lastPointer = { y: event.clientY };
  };

  /**
   * ASYCO: bandera "el puntero está DENTRO del rectángulo del dropdown".
   * Mientras sea true, el menú NO se cierra ni cambia por ningún motivo
   * (regla pedida por Julian). Solo al sacar el mouse del panel se libera.
   */
  #insideSubmenu = false;

  #submenuOver = (event) => {
    if (!(event.target instanceof Element)) return;
    const sub = event.target.closest('.menu-list__submenu');
    if (!sub) return;
    this.#insideSubmenu = true;
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    if (this.#swapTimer) {
      clearTimeout(this.#swapTimer);
      this.#swapTimer = null;
      this.#pendingItem = null;
    }
  };

  #submenuOut = (event) => {
    if (!(event.target instanceof Element)) return;
    const sub = event.target.closest('.menu-list__submenu');
    if (!sub) return;
    // Movimiento interno dentro del panel: ignorar
    if (event.relatedTarget instanceof Node && sub.contains(event.relatedTarget)) return;
    this.#insideSubmenu = false;
    // Salió del rectángulo: programar cierre normal
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate();
    }, 400);
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointermove', this.#trackPointer, { passive: true });
    this.addEventListener('pointerover', this.#submenuOver, { passive: true });
    this.addEventListener('pointerout', this.#submenuOut, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointermove', this.#trackPointer);
    this.removeEventListener('pointerover', this.#submenuOver);
    this.removeEventListener('pointerout', this.#submenuOut);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    // ASYCO: mismo cierre diferido para el overflow ("Más")
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate();
    }, 300);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // ASYCO: re-entrar al item o a su submenu cancela el cierre diferido
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) {
      // ASYCO: volver al item activo (o entrar a su submenu) cancela el swap pendiente
      if (this.#swapTimer) {
        clearTimeout(this.#swapTimer);
        this.#swapTimer = null;
        this.#pendingItem = null;
      }
      return;
    }

    // ASYCO: con un menú ya abierto, el cambio a otro item se difiere.
    // Hover intent: si el mouse viene BAJANDO (rumbo al dropdown), espera 500ms
    // (cruce diagonal lento no roba el menú); horizontal/subiendo = 150ms (ágil).
    if (this.#state.activeItem) {
      const falling =
        this.#prevPointer && this.#lastPointer && this.#lastPointer.y - this.#prevPointer.y > 2;
      if (this.#swapTimer) clearTimeout(this.#swapTimer);
      this.#pendingItem = item;
      const pendingEvent = event;
      this.#swapTimer = setTimeout(() => {
        this.#swapTimer = null;
        this.#pendingItem = null;
        // ASYCO: dentro del rectángulo del dropdown no se cambia de menú
        if (this.#insideSubmenu) return;
        this.#doActivate(pendingEvent, item);
      }, falling ? 500 : 150);
      return;
    }

    this.#doActivate(event, item);
  };

  /**
   * Activación real del item (cuerpo original de activate).
   * @param {PointerEvent | FocusEvent} event
   * @param {HTMLElement} item
   */
  #doActivate = (event, item) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // ASYCO: al cambiar de panel, el puntero ya no está en el dropdown anterior
    this.#insideSubmenu = false;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // ASYCO: salir del trigger cruzado antes de 150ms cancela el swap pendiente
    if (this.#swapTimer) {
      const leavingItem = findMenuItem(event.target);
      if (leavingItem && leavingItem === this.#pendingItem) {
        clearTimeout(this.#swapTimer);
        this.#swapTimer = null;
        this.#pendingItem = null;
      }
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // ASYCO: cierre diferido 400ms — da tiempo a cruzar el gap hacia el dropdown
    // incluso con movimiento lento. Si el puntero re-entra, activate() lo cancela.
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate();
    }, 400);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    // ASYCO: con el puntero dentro del rectángulo del dropdown, NUNCA cerrar
    if (this.#insideSubmenu) return;
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
